import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import TextArea from "@app/components/textarea";
import { Images } from "@assets/images";
import { Form, Formik, FormikContextType } from "formik";
import Chapter from "../components/chapter.component";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { BookOptionSelect } from "@app/components/book-option-select";
import { CustomSelect } from "@app/components/select/custom-select";
import {
  BookOption,
  BUCKET_URL_KEY,
  DATE_TIME_FORMAT_2,
  MAX_SIZE_UPLOAD,
  REGEX_URL,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import CategoryService from "@app/services/http/category.service";
import {
  BackgroundMusic,
  BookElementChildren,
  CreateBook,
  CreateBookForm,
  LabelValue,
  ModelTrain,
} from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import BackgroundMusicService from "@app/services/http/background-music.service";
import {
  ChangeEvent,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { convertDate } from "@app/utils/util";
import { createBookValidationSchema } from "@app/validations";
import { useNavigate } from "react-router-dom";
import BookService from "@app/services/http/book.service";
import { addToast } from "@app/components/toast/toast.service";
import VoiceConvertService from "@app/services/http/voice-convert.service";
import CustomAsyncMultiSelect from "@app/components/custom-async-multi-select";
import TryListeningCheckbox from "../components/try-listening-checkbox.component";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import UploadMediaService from "@app/services/http/upload-media.service";
import StorageService from "@core/services/storage";
import Audiobook from "@app/components/audiobook";
import { useDispatch, useSelector } from "react-redux";
import {
  sendAudiobook,
  showAudiobookPanel,
  selectAudiobook,
} from "@app/store/audiobook";
import { ScrollToError } from "@core/components/scroll-to-error/scroll-to-error.component";
import clsx from "clsx";
import { EndpointConversion } from "@app/types/voice-convert.type";
import AuthorService from "@app/services/http/author.service";
import { selectAuth } from "@app/store/auth";
import { openPortalDialog } from "@app/services/modal.service";
import AiThumbnailModal from "@app/components/ai-thumbnail-modal";
import EbookPreviewModal from "@app/components/ebook-preview-modal";

function CreateBookPage() {
  const bookMediaTypeOptions = [
    { label: "Audio", value: "1" },
    { label: "Ebook", value: "2" },
    { label: "Audio + Ebook", value: "3" },
  ];
  const inputThumbnailRef = useRef<HTMLInputElement>(null);
  const previewThumbnailRef = useRef<HTMLImageElement>(null);
  const inputAdditionalThumbnailRef = useRef<HTMLInputElement>(null);
  const formRef = createRef<FormikContextType<CreateBookForm>>();
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);
  const [bgAudioSelectedId, setBgAudioSelectedId] = useState(0);
  const [bgAudios, setBgAudios] = useState<BackgroundMusic[]>([]);
  const userInfo = useSelector(selectAuth);
  const dispatch = useDispatch();
  const { isPanelShow } = useSelector(selectAudiobook);

  const { abortController } = useCancellationRequest();

  const [isComingSoon, setIsComingSoon] = useState(false);
  const [isGenerateThumbnailsByAI, setIsGenerateThumbnailsByAI] =
    useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState("1");
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [ebookFile, setEbookFile] = useState<File | null>(null);
  const [ebookFileName, setEbookFileName] = useState("");
  const [modelTrains, setModelTrains] = useState<ModelTrain[]>([]);
  const [endpointConversions, setEndpointConversions] = useState<
    EndpointConversion[]
  >([]);
  const [additionalThumbnails, setAdditionalThumbnails] = useState<string[]>(
    []
  );
  const [errorMessage, setErrorMessage] = useState<{
    thumbnail: string;
    additionalThumbnail?: string;
    ebookFile?: string;
  }>({
    thumbnail: "",
    additionalThumbnail: "",
    ebookFile: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      VoiceConvertService.getEndpointConversions(abortController.signal),
      VoiceConvertService.getModelTrains(abortController.signal),
    ]).then(([endpointConversionsRes, modelTrainsRes]) => {
      setEndpointConversions(endpointConversionsRes.data.items);
      setModelTrains(modelTrainsRes.data.items);
    });
  }, []);

  const getCategoriesOptions = (inputValue: string) => {
    return new Promise<LabelValue[]>((resolve) => {
      CategoryService.getList(
        { keyword: inputValue.trim() },
        abortController.signal,
        false
      ).then((res) => {
        const data: LabelValue[] = res.data.items.map((item) => ({
          label: item.name,
          value: item.id,
        }));

        resolve(data);
      });
    });
  };

  const getMusicsOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BackgroundMusicService.getList(
        { keyword: inputValue.trim() },
        abortController.signal,
        false
      ).then((res) => {
        const data: LabelValue[] = res.data.items.map((item) => ({
          label: item.name,
          value: item.id,
        }));

        setBgAudios(res.data.items);

        callback(data);
      });
    },
    []
  );

  const onAddThumbnailFileClick = () => {
    if (inputThumbnailRef.current) {
      inputThumbnailRef.current.click();
    }
  };

  const onAddAdditionalThumbnailFilesClick = () => {
    if (inputAdditionalThumbnailRef.current) {
      inputAdditionalThumbnailRef.current.click();
    }
  };

  const handleThumbnailFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      if (previewThumbnailRef.current) {
        previewThumbnailRef.current.src = "";
      }

      setThumbnail(null);

      const newErrorMessage = {
        ...errorMessage,
        thumbnail: "",
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      if (previewThumbnailRef.current) {
        previewThumbnailRef.current.src = "";
      }

      setThumbnail(null);

      const newErrorMessage = {
        ...errorMessage,
        thumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    // Upload the file immediately
    const formData = new FormData();

    formData.append("medias", file);

    UploadMediaService.upload({ medias: [file] })
      .then((response) => {
        // Update the preview with the uploaded image URL
        if (previewThumbnailRef.current && response.data) {
          const uploadedImageUrl = s3Url + response.data.items[0].url;

          previewThumbnailRef.current.src = uploadedImageUrl;
        }

        // Store the URL string instead of the file
        if (response.data && response.data.items[0]) {
          setThumbnail(response.data.items[0].url);
        }

        const newErrorMessage = {
          ...errorMessage,
          thumbnail: "",
        };

        setErrorMessage(newErrorMessage);
      })
      .catch(() => {
        setThumbnail(null);

        const newErrorMessage = {
          ...errorMessage,
          thumbnail: "",
        };

        setErrorMessage(newErrorMessage);
      });
  };

  const handleAdditionalThumbnailFilesChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (!files?.length) {
      setAdditionalThumbnails([]);

      return;
    }

    let isValid = true;
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      newFiles.push(file);

      if (file.size > MAX_SIZE_UPLOAD) {
        isValid = false;
      }
    }

    if (!isValid) {
      const newErrorMessage = {
        ...errorMessage,
        additionalThumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    try {
      // Upload files to server
      const response = await UploadMediaService.upload({
        medias: newFiles,
      });

      if (response.data?.items) {
        // Extract URLs from the response
        let uploadedUrls: string[] = [];

        if (response.data.items && response.data.items.length > 0) {
          // If we have multiple items
          uploadedUrls = response.data.items.map((item) => item.url);
        }

        // Set the uploaded URLs to state
        setAdditionalThumbnails(uploadedUrls);
      }
    } catch (error) {
      const newErrorMessage = {
        ...errorMessage,
        additionalThumbnail: "Failed to upload files. Please try again.",
      };

      setErrorMessage(newErrorMessage);
    }
  };

  const handleBookOptionChange = (e: number | string) => {
    if (formRef.current && BookOption.FREE.toString() === e) {
      formRef.current.setFieldValue("isTryListening", false);
    }
  };

  const supportsEbook = (mediaType?: string | number) =>
    String(mediaType) === "2" || String(mediaType) === "3";

  const handleMediaTypeChange = (value: string | number) => {
    const nextValue = String(value);

    setSelectedMediaType(nextValue);

    if (!supportsEbook(nextValue)) {
      setEbookFile(null);
      setEbookFileName("");
      setErrorMessage((prev) => ({ ...prev, ebookFile: "" }));
    }
  };

  const handleEbookFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setEbookFile(null);
      setEbookFileName("");

      return;
    }

    if (file.type !== "application/pdf") {
      setErrorMessage((prev) => ({
        ...prev,
        ebookFile: "Chỉ hỗ trợ file PDF",
      }));
      setEbookFile(null);
      setEbookFileName("");

      return;
    }

    setEbookFile(file);
    setEbookFileName(file.name);
    setErrorMessage((prev) => ({ ...prev, ebookFile: "" }));
  };

  const handlePreviewLocalEbook = () => {
    if (!ebookFile) {
      return;
    }

    const previewUrl = URL.createObjectURL(ebookFile);
    const modalRef = openPortalDialog(EbookPreviewModal, {
      title: ebookFile.name,
      previewUrl,
    });

    modalRef.afterClosed().subscribe(() => {
      URL.revokeObjectURL(previewUrl);
    });
  };

  const handleComingSoonChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsComingSoon(e.target.checked);
  };

  const handleGenerateThumbnailsByAIChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked;

    setIsGenerateThumbnailsByAI(isChecked);

    if (isChecked) {
      // Need to save the book first to get bookId, then show AI modal
      if (formRef.current) {
        const currentValues = formRef.current.values;

        // Validate required fields before proceeding
        if (!currentValues.name.trim()) {
          addToast({
            text: "Vui lòng nhập tên sách trước khi tạo bìa AI",
            status: "inValid",
          });

          setIsGenerateThumbnailsByAI(false);

          return;
        }

        if (!currentValues.categoryIds.length) {
          addToast({
            text: "Vui lòng chọn danh mục trước khi tạo bìa AI",
            status: "inValid",
          });

          setIsGenerateThumbnailsByAI(false);

          return;
        }

        if (!currentValues.bookOption) {
          addToast({
            text: "Vui lòng chọn cách phân phối trước khi tạo bìa AI",
            status: "inValid",
          });

          setIsGenerateThumbnailsByAI(false);

          return;
        }

        if (!currentValues.description.trim()) {
          addToast({
            text: "Vui lòng nhập mô tả sách trước khi tạo bìa AI",
            status: "inValid",
          });

          setIsGenerateThumbnailsByAI(false);

          return;
        }

        // Create a minimal book first to get bookId
        handleAIThumbnailGeneration(currentValues);
      }
    }
  };

  const handleAIThumbnailGeneration = async (values: CreateBookForm) => {
    try {
      const shouldUploadEbook = supportsEbook(values.mediaType);

      if (shouldUploadEbook && !ebookFile) {
        setErrorMessage((prev) => ({
          ...prev,
          ebookFile: SystemMessage.INPUT_REQUIRED,
        }));

        return;
      }

      const thumbnails: string[] = [];

      if (thumbnail) {
        thumbnails.push(thumbnail);
      }

      if (additionalThumbnails.length) {
        thumbnails.push(...additionalThumbnails);
      }

      const createBook: CreateBook = {
        name: values.name.trim(),
        price: Number(values.price) || 0,
        ebookPrice: Number(values.ebookPrice) || 0,
        mediaType: Number(values.mediaType) || 1,
        oldPrice: Number(values.oldPrice) || 0,
        description: values.description.trim(),
        thumbnails,
        categoryIds: (values.categoryIds as any).map((item: LabelValue) =>
          Number(item.value)
        ),
        authorIds: values.authorIds.map((item: LabelValue) =>
          Number(item.value)
        ),
        bookOption: values.bookOption,
        musicId: Number(values.musicId?.value) || 0,
        linkStore: values.linkStore,
        isPopular: values.isPopular,
        isTryListening: !!values.isTryListening,
        isDraft: true, // Save as draft initially
        timePublish: 0,
        elements: values.elements,
      };

      // Transform modelConverts in elements
      if (createBook.elements && createBook.elements.length > 0) {
        createBook.elements = createBook.elements.map((element) => {
          if (element.modelConverts && element.modelConverts.length > 0) {
            element.modelConverts = element.modelConverts.map((convert) => ({
              endpointConvertId: Number(convert.endpoint?.value) || 0,
              modelConvertId: Number(convert.model?.value) || 0,
            }));
          }

          if (element.children && element.children.length > 0) {
            element.children = element.children.map((child) => {
              if (child.modelConverts && child.modelConverts.length > 0) {
                child.modelConverts = child.modelConverts.map((convert) => ({
                  endpointConvertId: Number(convert.endpoint?.value) || 0,
                  modelConvertId: Number(convert.model?.value) || 0,
                }));
              }

              return child;
            });
          }

          return element;
        });
      }

      // Save the book first
      const bookResponse = await BookService.create(createBook);
      const bookId = (bookResponse as any).data.id;

      if (shouldUploadEbook && ebookFile) {
        await BookService.uploadEbookFile(bookId, ebookFile);
      }

      // Open AI thumbnail modal
      const modalRef = openPortalDialog(AiThumbnailModal, {
        bookName: values.name,
        authorName: values.authorName || userInfo.fullName || "",
        onSubmit: async (aiData: any) => {
          try {
            await BookService.thumbnailByAI(bookId, aiData);
            addToast({ text: "Đang tạo bìa AI, vui lòng chờ..." });
            navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
          } catch (error) {
            addToast({ text: "Có lỗi khi tạo bìa AI", status: "inValid" });
          }
        },
      });

      modalRef.afterClosed().subscribe(() => {
        setIsGenerateThumbnailsByAI(false);
      });
    } catch (error) {
      addToast({ text: "Có lỗi khi lưu sách", status: "inValid" });
      setIsGenerateThumbnailsByAI(false);
    }
  };

  const handleRemoveAdditionalThumbnail = (indexToRemove: number) => {
    setAdditionalThumbnails((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleRemoveMainThumbnail = () => {
    setThumbnail(null);

    if (previewThumbnailRef.current) {
      previewThumbnailRef.current.src = "";
    }

    const newErrorMessage = {
      ...errorMessage,
      thumbnail: "",
    };

    setErrorMessage(newErrorMessage);
  };

  const handleSubmit = (values: CreateBookForm) => {
    const shouldUploadEbook = supportsEbook(values.mediaType);

    if (!thumbnail) {
      const newErrorMessage = {
        ...errorMessage,
        thumbnail: SystemMessage.INPUT_REQUIRED,
      };

      setErrorMessage(newErrorMessage);

      const element = document.getElementById("thumbnail-upload");

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return;
    }

    if (shouldUploadEbook && !ebookFile) {
      setErrorMessage((prev) => ({
        ...prev,
        ebookFile: SystemMessage.INPUT_REQUIRED,
      }));

      return;
    }

    const thumbnails: string[] = [];

    if (thumbnail) {
      thumbnails.push(thumbnail);
    }

    if (additionalThumbnails.length) {
      thumbnails.push(...additionalThumbnails);
    }

    const createBook: CreateBook = {
      name: values.name.trim(),
      price: Number(values.price),
      ebookPrice: Number(values.ebookPrice) || 0,
      mediaType: Number(values.mediaType) || 1,
      oldPrice: Number(values.oldPrice),
      description: values.description.trim(),
      thumbnails,
      categoryIds: (values.categoryIds as any).map((item: LabelValue) =>
        Number(item.value)
      ),
      authorIds: values.authorIds.map((item: LabelValue) => Number(item.value)),
      bookOption: values.bookOption,
      musicId: Number(values.musicId?.value) || 0,
      linkStore: values.linkStore,
      isPopular: values.isPopular,
      isTryListening: !!values.isTryListening,
      isDraft: shouldUploadEbook ? true : values.isDraft,
      timePublish: isComingSoon
        ? convertDate(values.timePublish, DATE_TIME_FORMAT_2).getTime() / 1000
        : 0,
      elements: values.elements,
    };

    const transformChildrenRecursively = (
      children: BookElementChildren[]
    ): BookElementChildren[] => {
      return children.map((child) => {
        if (child.modelConverts && child.modelConverts.length > 0) {
          child.modelConverts = child.modelConverts.map((convert) => ({
            endpointConvertId: Number(convert.endpoint?.value) || 0,
            modelConvertId: Number(convert.model?.value) || 0,
          }));
        }

        if (child.children && child.children.length > 0) {
          child.children = transformChildrenRecursively(child.children);
        }

        return child;
      });
    };

    if (createBook.elements && createBook.elements.length > 0) {
      createBook.elements = createBook.elements.map((element) => {
        if (element.modelConverts && element.modelConverts.length > 0) {
          element.modelConverts = element.modelConverts.map((convert) => ({
            endpointConvertId: Number(convert.endpoint?.value) || 0,
            modelConvertId: Number(convert.model?.value) || 0,
          }));
        }

        // Also transform modelConverts in children if they exist
        if (element.children && element.children.length > 0) {
          element.children = transformChildrenRecursively(element.children);
        }

        return element;
      });
    }

    BookService.create(createBook).then(async (response: any) => {
      if (shouldUploadEbook && ebookFile) {
        await BookService.uploadEbookFile(response.data.id, ebookFile);
        addToast({
          text: "Đã lưu sách dạng nháp và bắt đầu xử lý PDF ebook",
        });
      } else {
        addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
      }

      navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
    });
  };

  const handleCancelClick = () => {
    navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
  };

  const bgAudioSelectedSrc = useMemo(() => {
    const selected = bgAudios.find((item) => item.id === bgAudioSelectedId);

    return selected ? selected.link : "";
  }, [bgAudioSelectedId, bgAudios]);

  const handleClickPreviewAudio = (chapterName: string, linkAudio?: string) => {
    if (linkAudio) {
      dispatch(showAudiobookPanel());
      dispatch(sendAudiobook({ chapterName, linkAudio }));
    }
  };

  const getAuthorsOptions = useCallback((inputValue: string) => {
    return new Promise<LabelValue[]>((resolve) => {
      AuthorService.getList(abortController.signal, false, {
        keyword: inputValue,
        sortType: "ASC",
      }).then((res) => {
        const data: LabelValue[] = res.data.items;

        resolve(data);
      });
    });
  }, []);

  return (
    <div className={clsx("py-6 px-12", { "pb-32": isPanelShow })}>
      <Formik
        innerRef={formRef}
        initialValues={{
          name: "",
          categoryIds: [],
          bookOption: "",
          price: "",
          ebookPrice: "0",
          mediaType: "1",
          oldPrice: "",
          linkStore: "",
          timePublish: "",
          description: "",
          isPopular: false,
          isDraft: false,
          isTryListening: false,
          elements: [
            {
              description: "",
              name: "",
              point: "",
              isAutoGenerateThumbnail: false,
              thumbnails: [],
              audios: [],
              modelConverts: [],
              children: [],
            },
          ],
          authorIds: [],
          musicId: null,
          authorName: userInfo.fullName || "",
          genreName: "",
        }}
        validationSchema={createBookValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        <Form>
          <div className="fixed top-[120px] right-12 z-50">
            <Button
              type="submit"
              label="Lưu"
              width={164}
              containerClassName="mr-2"
              className="h-[52px] rounded-lg border border-solid border-[#5570F1] bg-white font-semibold text-[#5570F1] shadow-lg"
              theme="primary"
            />
          </div>
          <ScrollToError />
          <div className="flex justify-between items-center ">
            <div className="text-[#343C6A]">Danh sách sách / Tạo mới</div>
          </div>
          <div className="flex gap-6">
            <div className="mt-6 rounded-xl bg-white p-7 flex-[6]">
              <FormControl name="name">
                <Input placeholder="Tên sách, tác phẩm" />
              </FormControl>
              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="categoryIds">
                    <CustomAsyncMultiSelect
                      promiseOptions={getCategoriesOptions}
                      placeholder="Danh mục"
                    />
                  </FormControl>
                </div>
                <div className="flex-1">
                  <FormControl name="bookOption">
                    <BookOptionSelect onChange={handleBookOptionChange} />
                  </FormControl>
                </div>
              </div>

              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="oldPrice">
                    <Input placeholder="Giá bìa" />
                  </FormControl>
                </div>
                <div className="flex-1">
                  <FormControl name="price">
                    <Input placeholder="Giá bán khuyến mãi" />
                  </FormControl>
                </div>
              </div>
              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="mediaType">
                    <CustomSelect
                      options={bookMediaTypeOptions}
                      defaultValue="1"
                      className="bg-color !h-[52px] rounded-lg px-2"
                      optionContainerClassName="overscroll-contain mt-1 rounded-b-lg overflow-y-auto"
                      optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                      labelSelectedClassName="black-50 !mx-3"
                      placeholder="Loại sách"
                      onChange={handleMediaTypeChange}
                    />
                  </FormControl>
                  <div className="mt-2 text-sm text-[#FF4D4F]">
                    Bạn có thể chọn loại sách để thao tác (Audio/Ebook)
                  </div>
                </div>
                {supportsEbook(selectedMediaType) && (
                  <div className="flex-1">
                    <FormControl name="ebookPrice">
                      <Input placeholder="Giá ebook" />
                    </FormControl>
                  </div>
                )}
              </div>
              <div className="flex mt-6 gap-[15px]">
                <div className="flex-1">
                  <FormControl name="authorIds">
                    <CustomAsyncMultiSelect
                      promiseOptions={getAuthorsOptions}
                      placeholder="Tác giả"
                    />
                  </FormControl>
                </div>
                <div className="flex-1 flex gap-1 items-center">
                  <div className="flex-1">
                    <FormControl name="musicId">
                      <CustomAsyncSelect
                        promiseOptions={getMusicsOptions}
                        placeholder="Nhạc nền bấm  xổ ra và nghe thử ở từng dòng"
                        onChange={(option) => {
                          if (option) {
                            setBgAudioSelectedId(option.value as number);
                          }
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className="flex items-center">
                    {bgAudioSelectedSrc?.match(REGEX_URL) && (
                      <div>
                        <abbr title="Nghe audio">
                          <img
                            className="w-8 cursor-pointer"
                            src={Images.PlayPreviewIcon.default}
                            alt=""
                            onClick={() =>
                              handleClickPreviewAudio(
                                "nghe nhạc nền",
                                bgAudioSelectedSrc
                              )
                            }
                          />
                        </abbr>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <FormControl name="linkStore">
                <Input className="mt-6" placeholder="Link mua sách giấy" />
              </FormControl>
              <FormControl name="description">
                <TextArea
                  textAreaClassName="bg-color border-none outline-none rounded-lg !px-4 !py-3"
                  className="mt-6"
                  width="100%"
                  height="auto"
                  rows={7}
                  placeholder="Nội dung giới thiệu sách, tác phẩm"
                  isStandardizeContent={true}
                />
              </FormControl>
              {supportsEbook(selectedMediaType) && (
                <div className="mt-6 rounded-xl border border-dashed border-[#CFD3D4] p-4">
                  <div className="text-sm font-semibold text-[#343C6A]">
                    File PDF Ebook
                  </div>
                  <div className="mt-2 text-sm text-[#83898C]">
                    Khi tạo sách có ebook, hệ thống sẽ lưu ở dạng nháp và xử lý
                    PDF trước khi bạn phát hành.
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleEbookFileChange}
                    />
                    {!!ebookFileName && (
                      <div className="text-sm text-[#38499B]">{ebookFileName}</div>
                    )}
                    {!!ebookFile && (
                      <Button
                        label="Preview Ebook"
                        width=""
                        className="rounded-xl px-5"
                        onClick={handlePreviewLocalEbook}
                      />
                    )}
                  </div>
                  {!!errorMessage.ebookFile && (
                    <div className="mt-2 text-xs text-[#FF4D4F]">
                      {errorMessage.ebookFile}
                    </div>
                  )}
                </div>
              )}
              <label htmlFor="">
                <div className="mt-6 flex">
                  <FormControl name="isPopular">
                    <Checkbox
                      label="Đánh dấu sách nên đọc"
                      labelClassName="text-sm text-[#83898C]"
                    />
                  </FormControl>
                </div>
              </label>
              <TryListeningCheckbox name="isTryListening" />
              <label htmlFor="">
                <div className="mt-3 flex">
                  <Checkbox
                    label="Sắp ra mắt"
                    labelClassName="text-sm text-[#83898C]"
                    onChange={handleComingSoonChange}
                  />
                </div>
              </label>
              {isComingSoon && (
                <FormControl name="timePublish">
                  <Input className="mt-3" type="datetime-local" />
                </FormControl>
              )}
              <h2 className="mt-6 mb-4 text-[#2C2D33] font-semibold text-[18px]">
                Phần đầu hoặc Chương
              </h2>
              <FormControl name="elements">
                <Chapter
                  arrayName="elements"
                  endpointConversions={endpointConversions}
                  modelTrains={modelTrains}
                />
              </FormControl>
            </div>
            <div className="flex-[4] mt-6 rounded-xl bg-white py-6 px-5">
              <label htmlFor="">
                <div className="mt-6 flex">
                  <Checkbox
                    label="Tạo bìa bằng AI (cần lưu sách trước khi sử dụng tính năng)"
                    checked={isGenerateThumbnailsByAI}
                    onChange={handleGenerateThumbnailsByAIChange}
                  />
                </div>
              </label>
              <div
                id="thumbnail-upload"
                className="bg-[#F4F5FA] h-[436px] rounded-lg flex flex-col items-center justify-center cursor-pointer relative"
                onClick={onAddThumbnailFileClick}
              >
                {thumbnail && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveMainThumbnail();
                    }}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold z-10 transition-colors"
                    title="Xóa ảnh"
                  >
                    ×
                  </button>
                )}
                {!thumbnail && (
                  <>
                    <div>
                      <img src={Images.UploadIcon.default} alt="" />
                    </div>
                    <div className="flex gap-3 items-center mt-4">
                      <div>
                        <img src={Images.UploadCloudIcon.default} alt="" />
                      </div>
                      <div className="primary-100">Tải lên ảnh bìa</div>
                    </div>
                    <div className="black-30 text-sm mt-3">
                      Tải lên ảnh bìa cho sản phẩm của bạn, kích thước chấp nhận
                      1080x1920px, định dạng JPG hoặc PNG
                    </div>
                  </>
                )}
                <img ref={previewThumbnailRef} alt="" />
              </div>
              <input
                ref={inputThumbnailRef}
                type="file"
                name="thumbnailFile"
                hidden
                onChange={handleThumbnailFileChange}
              />
              {errorMessage.thumbnail && (
                <div className="flex error-color text-xs">
                  {errorMessage.thumbnail}
                </div>
              )}
              <h2 className="mt-6 text-[#2C2D33] font-medium">
                Hình ảnh bổ sung
              </h2>
              {!!additionalThumbnails.length &&
                additionalThumbnails.map((item, index) => (
                  <div
                    key={`${item}-${index}`}
                    className="h-[147px] flex justify-center border border-dashed border-[#A6A8B1] rounded-lg mt-4 relative"
                  >
                    <button
                      type="button"
                      onClick={() => handleRemoveAdditionalThumbnail(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold z-10 transition-colors"
                      title="Xóa ảnh"
                    >
                      ×
                    </button>
                    <img className="h-full" alt="" src={s3Url + item} />
                  </div>
                ))}
              <div
                className="bg-[#F4F5FA] h-[147px] rounded-lg mt-3 flex flex-col items-center justify-center cursor-pointer"
                onClick={onAddAdditionalThumbnailFilesClick}
              >
                <div>
                  <img src={Images.UploadIcon.default} alt="" />
                </div>
                <div className="flex gap-3 items-center mt-4">
                  <div>
                    <img src={Images.UploadCloudIcon.default} alt="" />
                  </div>
                  <div className="primary-100">Tải lên ảnh bìa</div>
                </div>
              </div>
              <input
                ref={inputAdditionalThumbnailRef}
                type="file"
                name="additionalThumbnailFiles"
                hidden
                multiple
                onChange={handleAdditionalThumbnailFilesChange}
              />
              {errorMessage.additionalThumbnail && (
                <div className="flex error-color text-xs">
                  {errorMessage.additionalThumbnail}
                </div>
              )}
              <div className="h-[147px] border border-dashed border-[#A6A8B1] rounded-lg mt-4"></div>
            </div>
          </div>
          <div className="mt-7">
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isDraft">
                  <Checkbox
                    label="Lưu bản nháp"
                    labelClassName="text-sm text-[#696c6e]"
                  />
                </FormControl>
              </div>
            </label>
          </div>
          <div className="w-full mt-3 flex">
            <Button
              type="submit"
              label="Lưu"
              width={164}
              containerClassName="mr-2"
              className=" h-[52px] text-[#5570F1] rounded-lg border border-solid font-semibold border-[#5570F1]"
              theme="primary"
            />
            <Button
              type="button"
              label="Hủy"
              width={164}
              containerClassName="ml-2"
              className="bg-transparent h-[52px] text-[#5570F1] rounded-lg border font-semibold border-solid border-[#5570F1]"
              theme="secondary"
              onClick={handleCancelClick}
            />
          </div>
        </Form>
      </Formik>
      <div id="audiobook-root">
        <Audiobook />
      </div>
    </div>
  );
}

export default CreateBookPage;
